/**
 * React bindings for the translation runtime: `useTranslation`,
 * `<TranslatableText>`, `useLanguage`.
 *
 * Each hook resolves the silo and language the current datastore renders
 * under, then hands them to `translateLabel`, which this module re-exports for
 * callers that already have a language of their own.
 * `<TranslatableText>` catches translation errors and falls back to the label
 * key to prevent render crashes from malformed labels.
 */

import { type IsoLocale } from '@repo/shared/locale';
import { translateLabel } from '@repo/shared/translations/translate-label';
import { useCallback, useContext } from 'react';
import type { StyleProp, TextStyle } from 'react-native';
import { Text } from 'react-native';
import { useDatastore } from 'util/datastore';
import { LanguageContext } from 'util/datastoreContext';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { labelToKey } from 'util/stdtypes';
import { getDeploymentConfig } from 'util/util';

export { translateLabel };

export function useLanguage(): IsoLocale {
    return useContext(LanguageContext);
}

type TranslationReturn<T extends TLabel | null | undefined> = T extends NonNullable<T> ? string : null;

export function useTranslate() {
    const language = useLanguage();
    const datastore = useDatastore();
    const siloKey = datastore ? datastore.getSiloKey() : null;
    const deploymentConfig = getDeploymentConfig();
    const parentSilo = (siloKey && deploymentConfig.siloConfig?.[siloKey]?.parentSilo) || undefined;
    return useCallback(
        (label: TLabel, formatParams?: FormatParams) => {
            return translateLabel({ label, language, formatParams, parentSilo, siloKey });
        },
        [language, parentSilo, siloKey],
    );
}

export function useTranslation<T extends TLabel | null | undefined>(label: T, formatParams?: FormatParams): TranslationReturn<T>;
export function useTranslation(label: TLabel | null | undefined, formatParams?: FormatParams): string | null {
    const translate = useTranslate();
    return label == null ? null : translate(label, formatParams);
}

export type EllipsizeMode = 'head' | 'middle' | 'tail' | 'clip' | undefined;

export type TranslatableTextProps = {
    numberOfLines?: number;
    ellipsizeMode?: EllipsizeMode;
    text?: string;
    label?: TLabel;
    formatParams?: FormatParams;
    style?: StyleProp<TextStyle>;
    textRef?: React.RefObject<any>;
} & { [key: string]: any };

export function TranslatableText({
    numberOfLines,
    ellipsizeMode,
    text,
    label,
    formatParams,
    style,
    textRef,
    ...props
}: TranslatableTextProps) {
    try {
        const translatedLabel = useTranslation(label, formatParams);
        return (
            <Text ellipsizeMode={ellipsizeMode} numberOfLines={numberOfLines} ref={textRef} style={style} {...props}>
                {translatedLabel || (label ? labelToKey(label) : text)}
            </Text>
        );
    } catch (e) {
        console.error('Error translating ' + label, e);
        return (
            <Text ellipsizeMode={ellipsizeMode} numberOfLines={numberOfLines} ref={textRef} style={style} {...props}>
                {label ? labelToKey(label) : text}
            </Text>
        );
    }
}
